const { getElement, getElements } = window.BAO.Utils

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.error = error

    this.MESSAGES = {
      ADD_TOO_MANY: `Unfortunately, there isn't enough of this product available`,
      ALREADY_HAVE_ALL_STOCK: `You already have all of the remaining stock in your cart`,
      CANNOT_FIND_VARIANT: 'There was an error adding this product to cart.',
      GENERIC: 'There was an error adding this product to cart.',
      NOT_ENOUGH_IN_STOCK: `Unfortunately, there isn't enough of this product available`,
      SOLD_OUT: `Unfortunately, this product is sold out.`,
    }

    this.description = error?.description ?? ''
    this.status = error?.status ?? null

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone =
      this.pushCart.externalEls['error-template'].element.content.cloneNode(
        true,
      )
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.externalEls.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description.slice(0, 4) === 'All '
  }

  get genericError () {
    return (
      this.error.errors !== undefined &&
      this.error.errors.toLowerCase() === ' error'
    )
  }

  get soldOut () {
    return this.description.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredExternalElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
  }

  constructor () {
    super()

    this.sections = window.BAO.sectionRenderer.pushCartSections

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }

    this.timeout = null
    this.delay = 4200
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAddFromForm = this.onBeforeCartAddFromForm.bind(this)
    this.onAfterCartAddFromForm = this.onAfterCartAddFromForm.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAddFromForm.bind(this),
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAddFromForm.bind(this),
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onAfterCartChanged.bind(this),
    )

    const events = ['mouseenter', 'hover', 'touch']

    events.forEach((event) => {
      this.listeners.add(this.drawer, event, () => {
        if (this.timeout !== null) {
          clearTimeout(this.timeout)
          this.timeout = null
        }
      })
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAddFromForm,
    )
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAddFromForm,
    )
    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onAfterCartChanged.bind(this),
    )
  }

  onBeforeCartAddFromForm (event) {
    if (event.detail?.form?.dataset?.closeDrawersOnCartAdd === 'true') {
      this.drawers.activeDrawerKey = ''
    }
  }

  onAfterCartAddFromForm (event) {
    this.loading = true
    clearTimeout(this.timeout)

    // Don't show a drawer when the last item added was a gwp item
    const gwpItem = event.detail?.items?.find(
      (item) => item.properties?._gift_with_purchase === 'true',
    )

    if (gwpItem) {
      this.loading = true

      return
    }

    let showGwp = event.detail?.form?.dataset?.showGwp === 'true'

    const useCloseCartTimeout =
      event.detail?.form?.dataset?.useCloseCartTimeout

    if (useCloseCartTimeout === 'true' && !showGwp) {
      this.timeout = window.setTimeout(() => {
        this.drawers.activeDrawerKey = ''
      }, this.delay)
    }

    const formDataset = event.detail?.form.dataset
    showGwp =
      formDataset?.showGwp === 'true' && formDataset?.gwpAdded !== 'true'

    this.drawers.activeDrawerKey = showGwp
      ? 'drawer-gift-with-purchase'
      : this.drawer.getAttribute('key')
  }

  onAfterCartChanged (event) {
    this.drawer.dispatchEvent(new Event('drawer:hideSecondaryHeader'))

    if ('sections' in event.detail.state) {
      this.renderSections(event.detail.state.sections)
    }

    const variantName = event.detail?.form?.dataset?.variantName

    if (variantName !== undefined) {
      this.drawer.dispatchEvent(
        new CustomEvent('drawer:showSecondaryHeader', {
          detail: {
            data: {
              message: theme.strings.productAdded.replace(
                '[product]',
                variantName,
              ),
            },
          },
        }),
      )
    }

    // In the event that an item is removed from the cart, we check the remaining
    // items to see if a GWP item remains. If one doesn't, we remove data-gwp-added
    if (event.detail?.state?.items_removed?.length > 0) {
      const hasGwp = event.detail?.state?.items?.find(
        (item) => item.properties?._gift_with_purchase === 'true',
      )

      if (!hasGwp) {
        getElements(`${this.gwpProductFormSelector}`).forEach((form) => {
          form.removeAttribute('data-gwp-added')
        })
      }
    }

    this.loading = false
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    const key = this.drawer.getAttribute('key') ?? ''

    if (this.drawers.activeDrawerKey !== key) {
      this.drawers.activeDrawerKey = key
    }

    return PushCartError.handle(await res.json())
  }

  get drawers () {
    return this.closest('site-drawers')
  }

  get drawer () {
    return this.closest('site-drawer')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }

  get gwpProductFormSelector () {
    return `[data-product-form][data-gwp-added]`
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
